import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faCubesStacked } from '@fortawesome/free-solid-svg-icons';
import {
  equipmentTypeService,
  type EquipmentType,
  type CreateEquipmentTypePayload,
} from '@/services/equipment-type.service';
import { equipmentCategoryService } from '@/services/equipment-category.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

/** Exported for reuse by the inline creation modals (Phase C.3) — the intake
 *  workflow must never duplicate this form.
 *
 *  `onAddCategory` and `selectCategoryId` are optional and used only by the
 *  inline flow. The Master Data page passes neither, so its rendering and
 *  behaviour are unchanged. */
export function EquipmentTypeForm({
  initial,
  onSubmit,
  onCancel,
  saving,
  onAddCategory,
  selectCategoryId,
}: {
  initial?: Partial<CreateEquipmentTypePayload>;
  onSubmit: (d: CreateEquipmentTypePayload) => void;
  onCancel: () => void;
  saving: boolean;
  /** When supplied, renders a "+ Add" beside the Category select. The parent
   *  owns the modal so it can be a page-level sibling rather than nested. */
  onAddCategory?: () => void;
  /** Set by the parent after a category is created inline; selects it without
   *  disturbing any other field the operator has already filled in. */
  selectCategoryId?: string;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (selectCategoryId) setCategoryId(selectCategoryId);
  }, [selectCategoryId]);

  const { data: categories } = useQuery({
    queryKey: ['equipment-categories-all'],
    queryFn: () => equipmentCategoryService.list({ pageSize: 100 }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!categoryId) errs.categoryId = 'Category is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({ name, categoryId, description: description || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Category" htmlFor="et-cat" required error={errors.categoryId}>
        <div className="flex items-center gap-2">
          <select
            id="et-cat"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={saving}
            className="flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50"
          >
            <option value="">Select a category...</option>
            {categories?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {onAddCategory && (
            <Button type="button" variant="secondary" onClick={onAddCategory} disabled={saving}>
              + Add
            </Button>
          )}
        </div>
      </FormField>
      <FormField label="Name" htmlFor="et-name" required error={errors.name}>
        <Input
          id="et-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Chiller, AHU, Panel..."
          disabled={saving}
        />
      </FormField>
      <FormField label="Description" htmlFor="et-desc">
        <Textarea
          id="et-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={saving}
        />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function EquipmentTypesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EquipmentType | null>(null);

  const { data: categories } = useQuery({
    queryKey: ['equipment-categories-all'],
    queryFn: () => equipmentCategoryService.list({ pageSize: 100 }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-types', page, debouncedSearch],
    queryFn: () =>
      equipmentTypeService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['equipment-types'] });

  const createMutation = useMutation({
    mutationFn: equipmentTypeService.create,
    onSuccess: () => {
      toast.success('Equipment type created');
      setModalOpen(false);
      invalidate();
    },
    onError: () => toast.error('Failed to create equipment type'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateEquipmentTypePayload> }) =>
      equipmentTypeService.update(id, payload),
    onSuccess: () => {
      toast.success('Equipment type updated');
      setModalOpen(false);
      setEditTarget(null);
      invalidate();
    },
    onError: () => toast.error('Failed to update equipment type'),
  });

  const deleteMutation = useMutation({
    mutationFn: equipmentTypeService.remove,
    onSuccess: () => {
      toast.success('Equipment type deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete equipment type'),
  });

  const getCategoryName = (id: string) =>
    categories?.items.find((c) => c.id === id)?.name ?? id.slice(0, 8);

  const handleDelete = async (t: EquipmentType) => {
    if (await confirmDelete(t.name)) deleteMutation.mutate(t.id);
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (t: EquipmentType) => {
    setEditTarget(t);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipment Types"
        description="Equipment types within each category"
        onAdd={can('equipment-type:create') ? openCreate : undefined}
        addLabel="Add Type"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search equipment types..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[540px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Category</th>
                  <th className="px-4 py-3 font-medium text-foreground">Description</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium text-foreground">{t.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {getCategoryName(t.categoryId)}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-muted-foreground">
                      {t.description ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${t.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
                      >
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('equipment-type:update') && (
                          <button
                            onClick={() => openEdit(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${t.name}`}
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('equipment-type:delete') && (
                          <button
                            onClick={() => handleDelete(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${t.name}`}
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faCubesStacked}
          title={search ? 'No types match your search' : 'No equipment types yet'}
          description={search ? 'Try a different search term.' : 'Add your first equipment type.'}
          action={
            !search && can('equipment-type:create') ? (
              <Button onClick={openCreate}>Add Type</Button>
            ) : undefined
          }
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Equipment Type' : 'Add Equipment Type'}
      >
        <EquipmentTypeForm
          initial={
            editTarget
              ? {
                  name: editTarget.name,
                  categoryId: editTarget.categoryId,
                  description: editTarget.description ?? undefined,
                }
              : undefined
          }
          onSubmit={(p) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload: p })
              : createMutation.mutate(p)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
